import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { MDBIcon } from 'mdb-react-ui-kit';

// Styled Components
const slideIn = keyframes`
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.8);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 25px rgba(37, 211, 102, 0.6);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const WhatsAppContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;

  /* Desktop */
  @media (min-width: 1025px) {
    bottom: 30px;
    right: 30px;
  }

  /* Tablet */
  @media (min-width: 768px) and (max-width: 1024px) {
    bottom: 25px;
    right: 25px;
  }

  /* Mobile */
  @media (max-width: 767px) {
    bottom: 20px;
    right: 20px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    bottom: 40px;
    right: 40px;
  }
`;

const WhatsAppButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25D366, #128C7E);
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  animation: ${float} 3s ease-in-out infinite;

  &:hover {
    animation: ${pulse} 1s ease-in-out infinite;
    transform: scale(1.1);
  }

  /* Desktop */
  @media (min-width: 1025px) {
    width: 70px;
    height: 70px;
    font-size: 32px;
  }

  /* Tablet */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 65px;
    height: 65px;
    font-size: 30px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    width: 80px;
    height: 80px;
    font-size: 36px;
  }
`;

const CommunityWindow = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 3px solid #25D366;
  position: absolute;
  bottom: 80px;
  right: 0;
  animation: ${props => props.$isClosing ? slideOut : slideIn} 0.3s ease forwards;

  /* Desktop */
  @media (min-width: 1025px) {
    width: 400px;
    bottom: 90px;
  }

  /* Tablet */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 380px;
    bottom: 85px;
  }

  /* Mobile */
  @media (max-width: 767px) {
    width: 350px;
    bottom: 75px;
    right: -10px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    width: 450px;
    bottom: 100px;
  }

  /* Small Mobile */
  @media (max-width: 480px) {
    width: 320px;
    right: -20px;
  }

  /* Extra Small Mobile */
  @media (max-width: 360px) {
    width: 300px;
    right: -30px;
  }
`;

const CommunityHeader = styled.div`
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  padding: 25px 20px;
  text-align: center;
  position: relative;

  /* Desktop */
  @media (min-width: 1025px) {
    padding: 30px 25px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    padding: 35px 30px;
  }
`;

const HeaderIcon = styled.div`
  width: 70px;
  height: 70px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: #25D366;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  /* Desktop */
  @media (min-width: 1025px) {
    width: 80px;
    height: 80px;
    font-size: 36px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    width: 90px;
    height: 90px;
    font-size: 40px;
  }
`;

const HeaderTitle = styled.h3`
  margin: 0 0 10px 0;
  font-weight: 700;
  font-size: 22px;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 24px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    font-size: 26px;
  }

  /* Mobile */
  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  opacity: 0.95;
  font-size: 15px;
  font-weight: 500;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 16px;
  }

  /* Mobile */
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const CommunityContent = styled.div`
  padding: 25px;
  background: #f8f9fa;

  /* Desktop */
  @media (min-width: 1025px) {
    padding: 30px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    padding: 35px;
  }
`;

const CommunityInfo = styled.div`
  background: white;
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 20px;
  border-left: 5px solid #042e2e;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  /* Desktop */
  @media (min-width: 1025px) {
    padding: 25px;
    margin-bottom: 25px;
  }
`;

const InfoTitle = styled.h4`
  color: #042e2e;
  margin: 0 0 12px 0;
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 10px;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 17px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    font-size: 18px;
  }
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #555;
  font-size: 14px;
  line-height: 1.6;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 15px;
    padding-left: 25px;
  }
`;

const InfoItem = styled.li`
  margin-bottom: 8px;
  position: relative;

  &::marker {
    color: #25D366;
  }
`;

const JoinButton = styled.button`
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border: none;
  border-radius: 15px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
  margin-top: 10px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(37, 211, 102, 0.5);
  }

  &:active {
    transform: translateY(-1px);
  }

  /* Desktop */
  @media (min-width: 1025px) {
    padding: 20px;
    font-size: 18px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) {
    padding: 22px;
    font-size: 19px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 18px;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  /* Desktop */
  @media (min-width: 1025px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const MemberCount = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 14px;
  margin-top: 15px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(10px);
  font-weight: 600;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 15px;
    padding: 12px 24px;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin: 20px 0;

  /* Desktop */
  @media (min-width: 1025px) {
    gap: 18px;
    margin: 25px 0;
  }

  /* Mobile */
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FeatureItem = styled.div`
  background: white;
  padding: 20px 15px;
  border-radius: 12px;
  text-align: center;
  border: 2px solid #f0f0f0;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #25D366;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(37, 211, 102, 0.15);
  }

  /* Desktop */
  @media (min-width: 1025px) {
    padding: 25px 20px;
  }
`;

const FeatureIcon = styled.div`
  font-size: 24px;
  color: #25D366;
  margin-bottom: 10px;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 28px;
  }
`;

const FeatureText = styled.div`
  font-size: 13px;
  color: #555;
  font-weight: 600;
  line-height: 1.4;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 14px;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 20px 0;
  padding: 15px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #042e2e;
  margin-bottom: 5px;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 22px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
  font-weight: 500;

  /* Desktop */
  @media (min-width: 1025px) {
    font-size: 13px;
  }
`;

// Main WhatsApp Community Component
function WhatsAppCommunity() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // WhatsApp community link - replace with your actual link
  const whatsappCommunityLink = "https://chat.whatsapp.com/YOUR_COMMUNITY_INVITE_LINK";

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1025);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const handleOpenCommunity = () => {
    setIsOpen(true);
    setIsClosing(false);
  };

  const handleCloseCommunity = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 250);
  };

  const handleJoinCommunity = () => {
    // Open WhatsApp community in new tab
    window.open(whatsappCommunityLink, '_blank', 'noopener,noreferrer');
    
    // Close the window after a short delay
    setTimeout(() => {
      handleCloseCommunity();
    }, 500);
  };

  const communityFeatures = [
    { icon: 'comments', text: 'Instant Help & Support' },
    { icon: 'users', text: 'Peer Community' },
    { icon: 'bell', text: 'Live Updates' },
    { icon: 'question-circle', text: 'Q&A Sessions' }
  ];

  return (
    <WhatsAppContainer>
      <WhatsAppButton 
        onClick={handleOpenCommunity}
        title="Join AICEE WhatsApp Community"
      >
        <MDBIcon fab icon="whatsapp" />
      </WhatsAppButton>

      {isOpen && (
        <CommunityWindow $isClosing={isClosing}>
          <CommunityHeader>
            <CloseButton onClick={handleCloseCommunity} title="Close">
              <MDBIcon fas icon="times" />
            </CloseButton>
            <HeaderIcon>
              <MDBIcon fab icon="whatsapp" />
            </HeaderIcon>
            <HeaderTitle>AICEE Student Community</HeaderTitle>
            <HeaderSubtitle>Connect with students & get support</HeaderSubtitle>
            <MemberCount>
              <MDBIcon fas icon="users" /> 1,200+ Active Members
            </MemberCount>
          </CommunityHeader>

          <CommunityContent>
            <StatsContainer>
              <StatItem>
                <StatNumber>98%</StatNumber>
                <StatLabel>Response Rate</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>24/7</StatNumber>
                <StatLabel>Active Support</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>5min</StatNumber>
                <StatLabel>Avg. Response</StatLabel>
              </StatItem>
            </StatsContainer>

            <CommunityInfo>
              <InfoTitle>
                <MDBIcon fas icon="star" />
                Community Benefits
              </InfoTitle>
              <InfoList>
                <InfoItem>Get instant help from peers and mentors</InfoItem>
                <InfoItem>Stay updated with important announcements</InfoItem>
                <InfoItem>Share study materials and resources</InfoItem>
                <InfoItem>Discuss exam preparation strategies</InfoItem>
                <InfoItem>Connect with students nationwide</InfoItem>
              </InfoList>
            </CommunityInfo>

            <FeatureGrid>
              {communityFeatures.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureIcon>
                    <MDBIcon fas icon={feature.icon} />
                  </FeatureIcon>
                  <FeatureText>{feature.text}</FeatureText>
                </FeatureItem>
              ))}
            </FeatureGrid>

            <CommunityInfo>
              <InfoTitle>
                <MDBIcon fas icon="shield-alt" />
                Community Guidelines
              </InfoTitle>
              <InfoList>
                <InfoItem>Be respectful and professional</InfoItem>
                <InfoItem>No spam or promotional content</InfoItem>
                <InfoItem>Keep discussions relevant to AICEE</InfoItem>
                <InfoItem>Help fellow students when possible</InfoItem>
              </InfoList>
            </CommunityInfo>

            <JoinButton onClick={handleJoinCommunity}>
              <MDBIcon fab icon="whatsapp" />
              Join WhatsApp Community
              {isDesktop && " (Opens in New Tab)"}
            </JoinButton>
          </CommunityContent>
        </CommunityWindow>
      )}
    </WhatsAppContainer>
  );
}

export default WhatsAppCommunity;